// Lightweight, structurally-typed graph helpers so both node components (React Flow store nodes)
// and the builder page (state nodes/edges) can share them without type wrangling.
type GraphNode = { id: string; type?: string }
type GraphEdge = { source: string; target: string }

// Outcome nodes only make sense after the action that produces the outcome. Each maps to the
// upstream node kind(s) it requires.
const OUTCOME_REQUIRES: Record<string, Set<string>> = {
  // A handoff opens a conversation as Send SMS does, so a reply wait is meaningful after either.
  waitForReply: new Set([`sendSms`, `aiAgentSms`]),
  waitForKeyword: new Set([`sendSms`, `aiAgentSms`]),
  waitForCallResult: new Set([`initiateCall`]),
}

/** Whether any node of one of `kinds` is reachable upstream of `nodeId`. */
export function hasUpstreamKind(
  nodeId: string,
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  kinds: ReadonlySet<string>,
): boolean {
  const kindById = new Map(nodes.map((node) => [node.id, node.type]))
  const incoming = new Map<string, string[]>()
  for (const edge of edges) {
    const sources = incoming.get(edge.target) ?? []
    sources.push(edge.source)
    incoming.set(edge.target, sources)
  }
  const seen = new Set<string>()
  const stack = [...(incoming.get(nodeId) ?? [])]
  while (stack.length > 0) {
    const id = stack.pop() as string
    if (seen.has(id)) continue
    seen.add(id)
    if (kinds.has(kindById.get(id) ?? ``)) return true
    stack.push(...(incoming.get(id) ?? []))
  }
  return false
}

/** An outcome node (wait-for-reply / wait-for-call-result) is "orphaned" when the action it depends
 *  on isn't reachable upstream — it can never resolve, so it's invalid. */
export function isOrphanOutcomeNode(
  nodeId: string,
  type: string | undefined,
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
): boolean {
  const required = type ? OUTCOME_REQUIRES[type] : undefined
  return required ? !hasUpstreamKind(nodeId, nodes, edges, required) : false
}

/** Count of outcome nodes missing their required upstream action — these block Activate. */
export function orphanOutcomeNodeCount(nodes: readonly GraphNode[], edges: readonly GraphEdge[]): number {
  return nodes.filter((node) => isOrphanOutcomeNode(node.id, node.type, nodes, edges)).length
}

/** The second outlet of a Create Task step (TRK-6035). Steps below it run alongside the main path,
 *  for the task that step made. */
export const TASK_PATH_HANDLE = `task`

/** Whether every route from the trigger down to `nodeId` passes through a Create Task's task path.
 *  Edit Task edits that path's task, so anywhere else it has no task to edit. */
export function isOnTaskPath(
  nodeId: string,
  nodes: readonly GraphNode[],
  edges: readonly (GraphEdge & { sourceHandle?: string | null })[],
): boolean {
  const kindById = new Map(nodes.map((node) => [node.id, node.type]))
  const incoming = new Map<string, (GraphEdge & { sourceHandle?: string | null })[]>()
  for (const edge of edges) incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge])
  const seen = new Set<string>()
  const stack = [nodeId]
  while (stack.length > 0) {
    const id = stack.pop() as string
    if (seen.has(id)) continue
    seen.add(id)
    const into = incoming.get(id) ?? []
    if (into.length === 0) return false
    for (const edge of into) {
      if (kindById.get(edge.source) === `createTask` && edge.sourceHandle === TASK_PATH_HANDLE) continue
      stack.push(edge.source)
    }
  }
  return true
}

/** Nodes with no path from the trigger — they're disconnected islands and never run. Returns empty
 *  when there's no trigger yet (that's a separate workflow-level issue; don't flag everything). */
export function unreachableNodeIds(nodes: readonly GraphNode[], edges: readonly GraphEdge[]): Set<string> {
  const trigger = nodes.find((node) => node.type === `trigger`)
  if (!trigger) return new Set()
  const outgoing = new Map<string, string[]>()
  for (const edge of edges) {
    const targets = outgoing.get(edge.source) ?? []
    targets.push(edge.target)
    outgoing.set(edge.source, targets)
  }
  const reachable = new Set<string>([trigger.id])
  const stack = [trigger.id]
  while (stack.length > 0) {
    const id = stack.pop() as string
    for (const next of outgoing.get(id) ?? []) {
      if (!reachable.has(next)) {
        reachable.add(next)
        stack.push(next)
      }
    }
  }
  return new Set(nodes.filter((node) => !reachable.has(node.id)).map((node) => node.id))
}

type BridgeEdge = { source: string; target: string; sourceHandle?: string | null }

/** Auto-connect model: there's no manual reconnect, so deleting a node must keep the chain intact.
 *  Bridge the node's incoming edges to its primary (first) outgoing target — preserving each incoming
 *  edge's `sourceHandle` (so a branch arm stays a branch arm). Returns the bridge edges to ADD (empty
 *  for a leaf, i.e. a node with no outgoing edge). The caller drops the deleted node's own edges. */
export function bridgeEdges(
  nodeId: string,
  edges: readonly BridgeEdge[],
): { source: string; sourceHandle?: string; target: string }[] {
  const primaryTarget = edges.find((edge) => edge.source === nodeId)?.target
  if (primaryTarget === undefined) return []
  return edges
    .filter((edge) => edge.target === nodeId)
    .map((edge) => ({ source: edge.source, sourceHandle: edge.sourceHandle ?? undefined, target: primaryTarget }))
}
